import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import adminService from '../../services/adminService';
import '../../styles/glass-morphism.css';

const WINDOWS = [7, 30, 90];

function changeLabel(metric) {
  if (!metric) return '';
  if (metric.change_pct === null || metric.change_pct === undefined) {
    return `${metric.previous} in the prior window`;
  }
  const sign = metric.change_pct >= 0 ? '+' : '';
  return `${sign}${metric.change_pct.toFixed(0)}% vs prior window`;
}

function formatWhen(value) {
  if (!value) return 'never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function DailyBars({ series, label }) {
  const max = Math.max(0, ...series.map((d) => d.count));
  return (
    <div style={styles.chart} className="glass-container">
      <div style={styles.chartHead}>
        <span style={styles.chartTitle}>{label}</span>
        <span style={styles.chartTotal}>{series.reduce((sum, d) => sum + d.count, 0)} total</span>
      </div>
      <div style={styles.bars} role="img" aria-label={`${label} per day`}>
        {series.map((d) => (
          <div key={d.date} style={styles.barColumn} title={`${d.date}: ${d.count}`}>
            <div style={{ ...styles.bar, height: `${max ? Math.max(4, (d.count / max) * 100) : 4}%`, opacity: d.count ? 1 : 0.25 }} />
          </div>
        ))}
      </div>
      <div style={styles.axis}>
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getDashboard(days);
      setStats(data.stats);
      setUsage(data.usage || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const planEntries = usage ? Object.entries(usage.users.by_plan || {}) : [];

  return (
    <div style={styles.container} className="cloud-background internal-page">
      <header style={styles.header} className="glass-container cloudnotes-app-header internal-page-header">
        <div style={styles.headerContent} className="internal-header-content">
          <h1 style={styles.logo}>Admin Dashboard</h1>
          <div style={styles.headerRight} className="internal-header-actions">
            <span style={styles.username}>Welcome, {user?.username}</span>
            <button className="internal-header-action" onClick={() => navigate('/notes')} style={styles.button}>
              Notes
            </button>
            <button className="internal-header-action" onClick={handleLogout} style={styles.button}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.content}>
          {error && <div style={styles.errorText}>Error: {error}</div>}

          {stats && (
            <>
              <div style={styles.statsGrid}>
                <div style={styles.statCard} className="glass-container">
                  <div style={styles.statValue}>{stats.total_users}</div>
                  <div style={styles.statLabel}>Total Users</div>
                  {usage && <div style={styles.statHint}>{usage.users.sso_linked} via SSO · {usage.users.local} local</div>}
                </div>
                <div style={styles.statCard} className="glass-container">
                  <div style={styles.statValue}>{stats.total_notes}</div>
                  <div style={styles.statLabel}>Total Notes</div>
                  {usage && <div style={styles.statHint}>{usage.users.with_notes} users have written a note</div>}
                </div>
                <div style={styles.statCard} className="glass-container">
                  <div style={styles.statValue}>{stats.total_categories}</div>
                  <div style={styles.statLabel}>Categories</div>
                  {usage && <div style={styles.statHint}>{usage.totals.favorites} favourite notes</div>}
                </div>
                <div style={styles.statCard} className="glass-container">
                  <div style={styles.statValue}>{stats.admin_users}</div>
                  <div style={styles.statLabel}>Admins</div>
                  {planEntries.length > 0 && (
                    <div style={styles.statHint}>
                      {planEntries.map(([tier, count]) => `${count} ${tier}`).join(' · ')}
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.sectionHead}>
                <h2 style={styles.sectionTitle}>Activity</h2>
                <div style={styles.windowPicker} role="group" aria-label="Window">
                  {WINDOWS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setDays(option)}
                      style={{ ...styles.windowButton, ...(option === days ? styles.windowButtonActive : {}) }}
                      aria-pressed={option === days}
                    >
                      {option}d
                    </button>
                  ))}
                </div>
              </div>

              {loading && !usage && <div style={styles.loadingText}>Loading...</div>}

              {usage && (
                <>
                  <div style={{ ...styles.statsGrid, opacity: loading ? 0.6 : 1 }}>
                    <div style={styles.statCard} className="glass-container">
                      <div style={styles.statValue}>{usage.signups.current}</div>
                      <div style={styles.statLabel}>New signups</div>
                      <div style={styles.statHint}>{changeLabel(usage.signups)}</div>
                    </div>
                    <div style={styles.statCard} className="glass-container">
                      <div style={styles.statValue}>{usage.active_users.current}</div>
                      <div style={styles.statLabel}>Active users</div>
                      <div style={styles.statHint}>edited a note · {changeLabel(usage.active_users)}</div>
                    </div>
                    <div style={styles.statCard} className="glass-container">
                      <div style={styles.statValue}>{usage.notes_created.current}</div>
                      <div style={styles.statLabel}>Notes created</div>
                      <div style={styles.statHint}>{changeLabel(usage.notes_created)}</div>
                    </div>
                    <div style={styles.statCard} className="glass-container">
                      <div style={styles.statValue}>{usage.notes_updated.current}</div>
                      <div style={styles.statLabel}>Notes edited</div>
                      <div style={styles.statHint}>{changeLabel(usage.notes_updated)}</div>
                    </div>
                  </div>

                  <div style={styles.chartsGrid}>
                    <DailyBars series={usage.daily.signups} label={`Signups per day (${usage.start} → ${usage.end})`} />
                    <DailyBars series={usage.daily.notes_created} label="Notes created per day" />
                  </div>

                  <div style={styles.meta}>
                    <span>Last signup: <strong>{formatWhen(usage.last_signup_at)}</strong></span>
                    <span>Last note activity: <strong>{formatWhen(usage.last_note_at)}</strong></span>
                  </div>

                  <div style={styles.tableCard} className="glass-container">
                    <div style={styles.chartHead}>
                      <span style={styles.chartTitle}>Recent signups</span>
                      <button
                        onClick={() => navigate('/admin/users')}
                        style={styles.linkButton}
                        className="internal-primary-action"
                      >
                        Manage Users
                      </button>
                    </div>
                    {usage.recent_signups.length === 0 ? (
                      <div style={styles.empty}>No users yet.</div>
                    ) : (
                      <div style={styles.tableWrap}>
                        <table style={styles.table}>
                          <thead>
                            <tr>
                              <th style={styles.th}>User</th>
                              <th style={styles.th}>Email</th>
                              <th style={styles.th}>Joined</th>
                              <th style={styles.th}>Sign-in</th>
                              <th style={styles.th}>Plan</th>
                              <th style={{ ...styles.th, textAlign: 'right' }}>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {usage.recent_signups.map((row) => (
                              <tr key={row.id}>
                                <td style={styles.td}>
                                  {row.username}
                                  {row.role === 'admin' && <span style={styles.badge}>admin</span>}
                                </td>
                                <td style={styles.td}>{row.email}</td>
                                <td style={styles.td}>{formatWhen(row.created_at)}</td>
                                <td style={styles.td}>{row.sso ? 'SSO' : 'password'}</td>
                                <td style={styles.td}>{row.plan_tier}</td>
                                <td style={{ ...styles.td, textAlign: 'right' }}>{row.note_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {loading && !stats && <div style={styles.loadingText}>Loading...</div>}
        </div>
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    boxSizing: 'border-box',
    background: '#f3f9fc',
    position: 'relative',
  },
  header: {
    flexShrink: 0,
    marginBottom: '20px',
    padding: '16px 24px',
    position: 'relative',
    zIndex: 1,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logo: {
    color: 'white',
    fontSize: '1.8rem',
    fontWeight: 'bold',
    margin: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  username: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '1rem',
  },
  button: {
    background: 'rgba(255, 255, 255, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    color: 'white',
    padding: '8px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    zIndex: 1,
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  loadingText: {
    color: '#334e68',
    fontSize: '1.5rem',
    textAlign: 'center',
    padding: '40px',
  },
  errorText: {
    color: '#b42318',
    fontSize: '1.2rem',
    textAlign: 'center',
    padding: '40px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '28px',
    transition: 'opacity 0.2s',
  },
  statCard: {
    padding: '28px 24px',
    textAlign: 'center',
  },
  statValue: {
    color: '#04658f',
    fontSize: '2.6rem',
    fontWeight: 'bold',
    marginBottom: '6px',
    lineHeight: 1.1,
  },
  statLabel: {
    color: '#52677c',
    fontSize: '0.9rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  statHint: {
    color: '#7a8da0',
    fontSize: '0.8rem',
    marginTop: '8px',
  },
  sectionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '8px 0 16px',
    gap: '16px',
    flexWrap: 'wrap',
  },
  sectionTitle: {
    color: '#1f3a52',
    fontSize: '1.2rem',
    margin: 0,
  },
  windowPicker: {
    display: 'inline-flex',
    border: '1px solid #cfe1ec',
    borderRadius: '8px',
    overflow: 'hidden',
    background: '#ffffff',
  },
  windowButton: {
    border: 'none',
    background: 'transparent',
    color: '#52677c',
    padding: '6px 14px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  windowButtonActive: {
    background: '#0778ad',
    color: '#ffffff',
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '20px',
    marginBottom: '20px',
  },
  chart: {
    padding: '18px 20px',
  },
  chartHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    gap: '12px',
    flexWrap: 'wrap',
  },
  chartTitle: {
    color: '#1f3a52',
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  chartTotal: {
    color: '#7a8da0',
    fontSize: '0.85rem',
  },
  bars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '2px',
    height: '110px',
  },
  barColumn: {
    flex: 1,
    height: '100%',
    display: 'flex',
    alignItems: 'flex-end',
  },
  bar: {
    width: '100%',
    background: 'linear-gradient(180deg, #2d9fd4, #0778ad)',
    borderRadius: '3px 3px 0 0',
    transition: 'height 0.2s',
  },
  axis: {
    display: 'flex',
    justifyContent: 'space-between',
    color: '#7a8da0',
    fontSize: '0.75rem',
    marginTop: '6px',
  },
  meta: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
    color: '#52677c',
    fontSize: '0.9rem',
    marginBottom: '20px',
  },
  tableCard: {
    padding: '18px 20px',
    marginBottom: '32px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  th: {
    textAlign: 'left',
    color: '#7a8da0',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: '8px 10px',
    borderBottom: '1px solid #e1edf4',
  },
  td: {
    padding: '10px',
    color: '#1f3a52',
    borderBottom: '1px solid #eef5f9',
    whiteSpace: 'nowrap',
  },
  badge: {
    marginLeft: '8px',
    padding: '2px 8px',
    borderRadius: '999px',
    background: '#e3f3fb',
    color: '#04658f',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  empty: {
    color: '#7a8da0',
    padding: '16px 0',
  },
  linkButton: {
    padding: '8px 16px',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'white',
    background: '#0778ad',
    cursor: 'pointer',
    border: '1px solid #0778ad',
    borderRadius: '8px',
  },
};

export default Dashboard;
